// Mind2Magic CRM — KPIs Module
// Dedicated analytics view with conversion funnel, trends, source attribution, team performance.
// Renders on-demand (not in renderAll) to avoid expensive recalculation on every data change.

import { STAGES, TYPE_LABELS } from './constants.js'
import { store } from './store.js'

const PAID_STAGES = ['pago_completo', 'documentacion', 'en_destino', 'finalizado', 'postventa']
const FUNNEL_GROUPS = [
    { key: 'consulta', label: 'Consulta', stages: ['nuevo', 'formulario', 'asesoria_solicitada', 'asesoria_agendada', 'asesoria_realizada'] },
    { key: 'cotizacion', label: 'Cotizacion', stages: ['cotizacion_solicitada', 'cotizacion', 'revision_cotizacion'] },
    { key: 'reserva', label: 'Reserva', stages: ['prereserva', 'reserva_abono'] },
    { key: 'pagado', label: 'Pagado', stages: ['pago_completo', 'documentacion'] },
    { key: 'viajando', label: 'Viajando', stages: ['en_destino', 'finalizado', 'postventa'] }
]
const EXCLUDED_FUNNEL = ['cancelado', 'no_califica', 'proximo_ano', 'no_compro']

// ========== PERIOD HELPERS ==========

function getKPIPeriodRange(period) {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    let start, end, prevStart, prevEnd

    if (period === 'mes') {
        start = new Date(today.getFullYear(), today.getMonth(), 1)
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59)
        prevStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
        prevEnd = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59)
    } else if (period === 'ultimoMes') {
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1)
        end = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59)
        prevStart = new Date(today.getFullYear(), today.getMonth() - 2, 1)
        prevEnd = new Date(today.getFullYear(), today.getMonth() - 1, 0, 23, 59, 59)
    } else if (period === 'trimestre') {
        const q = Math.floor(today.getMonth() / 3)
        start = new Date(today.getFullYear(), q * 3, 1)
        end = new Date(today.getFullYear(), q * 3 + 3, 0, 23, 59, 59)
        prevStart = new Date(today.getFullYear(), q * 3 - 3, 1)
        prevEnd = new Date(today.getFullYear(), q * 3, 0, 23, 59, 59)
    } else if (period === 'año') {
        start = new Date(today.getFullYear(), 0, 1)
        end = new Date(today.getFullYear(), 11, 31, 23, 59, 59)
        prevStart = new Date(today.getFullYear() - 1, 0, 1)
        prevEnd = new Date(today.getFullYear() - 1, 11, 31, 23, 59, 59)
    } else if (period === 'custom') {
        start = store.kpiCustomStart ? new Date(store.kpiCustomStart) : new Date(today.getFullYear(), 0, 1)
        end = store.kpiCustomEnd ? new Date(store.kpiCustomEnd + 'T23:59:59') : today
        const duration = end - start
        prevStart = new Date(start.getTime() - duration)
        prevEnd = new Date(start.getTime() - 1)
    } else {
        // fallback = 'todo'
        start = new Date(2020, 0, 1)
        end = new Date(2100, 0, 1)
        prevStart = null
        prevEnd = null
    }

    return { start, end, prevStart, prevEnd }
}

function filterByPeriod(items, start, end, dateField = 'createdAt') {
    return items.filter(item => {
        const d = item[dateField] ? new Date(item[dateField]) : null
        return d && d >= start && d <= end
    })
}

function pctChange(current, previous) {
    if (!previous || previous === 0) return current > 0 ? 100 : 0
    return ((current - previous) / previous * 100)
}

function deltaBadge(current, previous, suffix = '', invert = false) {
    if (previous === null || previous === undefined) return ''
    const pct = pctChange(current, previous)
    if (pct === 0) return `<span class="kpi-delta neutral">= 0%</span>`
    const isUp = pct > 0
    const isGood = invert ? !isUp : isUp
    return `<span class="kpi-delta ${isGood ? 'up' : 'down'}">${isUp ? '↑' : '↓'} ${Math.abs(pct).toFixed(0)}%${suffix}</span>`
}

// ========== CALCULATION FUNCTIONS ==========

function calcSummaryCards(allLeads, range) {
    const { start, end, prevStart, prevEnd } = range
    const all = [...allLeads, ...store.historico]
    const periodLeads = filterByPeriod(all, start, end)
    const prevLeads = prevStart ? filterByPeriod(all, prevStart, prevEnd) : null
    const showCompare = store.kpiShowCompare && prevLeads

    const active = periodLeads.filter(l => l.stage !== 'cancelado' && l.stage !== 'no_compro' && !l.wasCanceled)
    const prevActive = prevLeads ? prevLeads.filter(l => l.stage !== 'cancelado' && l.stage !== 'no_compro' && !l.wasCanceled) : null

    const newLeads = periodLeads.length
    const prevNew = prevLeads ? prevLeads.length : null

    const closed = active.filter(l => PAID_STAGES.includes(l.stage))
    const prevClosed = prevActive ? prevActive.filter(l => PAID_STAGES.includes(l.stage)) : null

    const convRate = active.length > 0 ? (closed.length / active.length * 100) : 0
    const prevConvRate = prevActive && prevActive.length > 0 ? (prevClosed.length / prevActive.length * 100) : null

    const totalVenta = closed.reduce((s, l) => s + (l.totalVenta || 0), 0)
    const prevVenta = prevClosed ? prevClosed.reduce((s, l) => s + (l.totalVenta || 0), 0) : null

    const totalM2M = closed.reduce((s, l) => s + (l.commission70 || 0), 0)
    const prevM2M = prevClosed ? prevClosed.reduce((s, l) => s + (l.commission70 || 0), 0) : null

    const avgTicket = closed.length > 0 ? totalVenta / closed.length : 0
    const prevAvgTicket = prevClosed && prevClosed.length > 0 ? prevVenta / prevClosed.length : null

    // Sales cycle: days from creation to fechaPagoFinal
    const withCycle = closed.filter(l => l.createdAt && l.fechaPagoFinal)
    const avgCycle = withCycle.length > 0
        ? withCycle.reduce((s, l) => s + (new Date(l.fechaPagoFinal) - new Date(l.createdAt)) / 86400000, 0) / withCycle.length
        : 0
    const prevWithCycle = prevClosed ? prevClosed.filter(l => l.createdAt && l.fechaPagoFinal) : []
    const prevAvgCycle = prevWithCycle.length > 0
        ? prevWithCycle.reduce((s, l) => s + (new Date(l.fechaPagoFinal) - new Date(l.createdAt)) / 86400000, 0) / prevWithCycle.length
        : null

    return { newLeads, prevNew, convRate, prevConvRate, totalVenta, prevVenta, totalM2M, prevM2M, avgTicket, prevAvgTicket, avgCycle, prevAvgCycle, showCompare }
}

function calcConversionFunnel(allLeads) {
    const all = [...allLeads, ...store.historico].filter(l => !EXCLUDED_FUNNEL.includes(l.stage) && l.stage !== 'cancelado' && l.stage !== 'no_compro' && !l.wasCanceled)
    const total = all.length
    if (total === 0) return { groups: FUNNEL_GROUPS.map(g => ({ ...g, count: 0, pct: 0 })), total: 0 }

    // Count leads that have REACHED at least this funnel group
    // A lead in 'pago_completo' has passed through consulta, cotizacion, reserva, AND pagado
    const stageOrder = FUNNEL_GROUPS.flatMap(g => g.stages)
    const stageToGroupIdx = {}
    FUNNEL_GROUPS.forEach((g, idx) => g.stages.forEach(s => { stageToGroupIdx[s] = idx }))

    const groupCounts = FUNNEL_GROUPS.map(() => 0)
    all.forEach(l => {
        const idx = stageToGroupIdx[l.stage]
        if (idx === undefined) return
        // This lead has reached groups 0 through idx
        for (let i = 0; i <= idx; i++) groupCounts[i]++
    })

    const groups = FUNNEL_GROUPS.map((g, i) => ({
        ...g,
        count: groupCounts[i],
        pct: i === 0 ? 100 : (groupCounts[i - 1] > 0 ? (groupCounts[i] / groupCounts[i - 1] * 100) : 0)
    }))

    return { groups, total: groupCounts[0] }
}

function calcSourceBreakdown(allLeads) {
    const all = [...allLeads, ...store.historico].filter(l => l.stage !== 'cancelado' && l.stage !== 'no_compro' && !l.wasCanceled)
    const bySource = {}
    all.forEach(l => {
        const src = l.source || 'Manual'
        if (!bySource[src]) bySource[src] = { total: 0, closed: 0, venta: 0 }
        bySource[src].total++
        if (PAID_STAGES.includes(l.stage)) {
            bySource[src].closed++
            bySource[src].venta += l.totalVenta || 0
        }
    })
    return Object.entries(bySource)
        .map(([name, data]) => ({ name, ...data, convRate: data.total > 0 ? (data.closed / data.total * 100) : 0 }))
        .sort((a, b) => b.total - a.total)
}

function calcTypeBreakdown(allLeads) {
    const all = [...allLeads, ...store.historico].filter(l => l.stage !== 'cancelado' && l.stage !== 'no_compro' && !l.wasCanceled)
    const byType = {}
    all.forEach(l => {
        const t = TYPE_LABELS[l.type] || 'Otro'
        if (!byType[t]) byType[t] = { count: 0, venta: 0 }
        byType[t].count++
        byType[t].venta += l.totalVenta || 0
    })
    return Object.entries(byType)
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.venta - a.venta)
}

function calcTeamPerformance(allLeads) {
    const all = [...allLeads, ...store.historico].filter(l => l.stage !== 'cancelado' && l.stage !== 'no_compro' && !l.wasCanceled)
    const byResp = {}
    all.forEach(l => {
        const r = l.responsable || 'Sin asignar'
        if (!byResp[r]) byResp[r] = { assigned: 0, closed: 0, m2m: 0, venta: 0 }
        byResp[r].assigned++
        if (PAID_STAGES.includes(l.stage)) {
            byResp[r].closed++
            byResp[r].m2m += l.commission70 || 0
            byResp[r].venta += l.totalVenta || 0
        }
    })
    return Object.entries(byResp)
        .map(([name, data]) => ({
            name, ...data,
            convRate: data.assigned > 0 ? (data.closed / data.assigned * 100) : 0,
            avgTicket: data.closed > 0 ? data.venta / data.closed : 0
        }))
        .sort((a, b) => b.m2m - a.m2m)
}

function calcMonthlyTrend(allLeads) {
    const all = [...allLeads, ...store.historico].filter(l => l.stage !== 'cancelado' && l.stage !== 'no_compro' && !l.wasCanceled)
    const now = new Date()
    const months = []

    for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
        const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59)
        const monthLabel = date.toLocaleDateString('es-CO', { month: 'short', year: '2-digit' })

        const created = all.filter(l => {
            const d = l.createdAt ? new Date(l.createdAt) : null
            return d && d >= date && d <= monthEnd
        }).length

        const closed = all.filter(l => {
            if (!PAID_STAGES.includes(l.stage)) return false
            const d = l.fechaPagoFinal ? new Date(l.fechaPagoFinal) : (l.createdAt ? new Date(l.createdAt) : null)
            return d && d >= date && d <= monthEnd
        })

        const m2m = closed.reduce((s, l) => s + (l.commission70 || 0), 0)

        months.push({ label: monthLabel, created, closed: closed.length, m2m })
    }

    return months
}

function calcStageBottlenecks(currentLeads) {
    const active = currentLeads.filter(l => l.stage !== 'cancelado' && l.stage !== 'no_compro' && !l.wasCanceled && l.stage !== 'no_califica')
    const byStage = {}
    const now = new Date()

    active.forEach(l => {
        const s = l.stage
        if (!byStage[s]) byStage[s] = { count: 0, totalDays: 0 }
        byStage[s].count++
        // Approximate time in stage using updatedAt (last modification)
        const updated = l.updatedAt ? new Date(l.updatedAt) : (l.createdAt ? new Date(l.createdAt) : now)
        const days = Math.max(0, (now - updated) / 86400000)
        byStage[s].totalDays += days
    })

    return STAGES
        .filter(s => byStage[s.id] && s.id !== 'cancelado' && s.id !== 'no_califica' && s.id !== 'no_compro')
        .map(s => ({
            id: s.id,
            name: s.name,
            color: s.color,
            count: byStage[s.id].count,
            avgDays: byStage[s.id].count > 0 ? (byStage[s.id].totalDays / byStage[s.id].count) : 0
        }))
}

function calcPaymentHealth(allLeads) {
    const active = [...allLeads, ...store.historico].filter(l =>
        l.stage !== 'cancelado' && l.stage !== 'no_compro' && !l.wasCanceled &&
        (l.abono > 0 || l.abonado > 0 || l.totalVenta > 0)
    )
    const now = new Date()

    const totalExpected = active.reduce((s, l) => s + (l.abono || 0), 0)
    const totalCollected = active.reduce((s, l) => s + (l.abonado || 0), 0)
    const collectionRate = totalExpected > 0 ? (totalCollected / totalExpected * 100) : 0

    const overdue = active.filter(l => {
        if (PAID_STAGES.includes(l.stage)) return false
        const due = l.fechaPagoFinal ? new Date(l.fechaPagoFinal) : null
        return due && due < now && (l.abonado || 0) < (l.totalVenta || 0)
    }).length

    const pendingPayment = active.filter(l =>
        !PAID_STAGES.includes(l.stage) && l.totalVenta > 0 && (l.abonado || 0) < (l.totalVenta || 0)
    ).length

    return { totalExpected, totalCollected, collectionRate, overdue, pendingPayment }
}

function calcFormConversion() {
    const subs = store.allFormSubmissions || []
    const sessions = store.allFormSessions || []

    const total = subs.length
    const converted = subs.filter(s => s.status === 'converted' || s.lead_id).length
    const pending = subs.filter(s => s.status === 'new' || s.status === 'pending').length
    const abandoned = sessions.filter(s => s.abandoned).length

    // By form type
    const byType = {}
    subs.forEach(s => {
        const type = s.form_type || 'unknown'
        if (!byType[type]) byType[type] = { total: 0, converted: 0 }
        byType[type].total++
        if (s.status === 'converted' || s.lead_id) byType[type].converted++
    })

    const types = Object.entries(byType)
        .map(([name, data]) => ({
            name: name.charAt(0).toUpperCase() + name.slice(1),
            ...data,
            rate: data.total > 0 ? (data.converted / data.total * 100) : 0
        }))
        .sort((a, b) => b.total - a.total)

    return { total, converted, pending, abandoned, types, convRate: total > 0 ? (converted / total * 100) : 0 }
}

// ========== RENDER ==========

function renderKPIs() {
    const container = document.getElementById('kpisContent')
    if (!container) return

    // Ensure ALL form submissions are loaded (sync.js only loads status='new' ones).
    // We need the full set for KPI analytics. Use _kpiFormsLoaded flag to avoid re-fetching.
    if (!store._kpiFormsLoaded && window.supabaseDB?.isConnected()) {
        store._kpiFormsLoaded = true // set immediately to prevent duplicate fetches
        Promise.all([
            window.supabaseDB.fetchAllFormSubmissions(),
            window.supabaseDB.fetchAllFormSessions(),
        ]).then(([subData, sessData]) => {
            if (subData) { store.allFormSubmissions = subData; store.formSubsLoaded = true }
            if (sessData) { store.allFormSessions = sessData; store.formSessionsLoaded = true }
            if (document.getElementById('kpisView')?.classList.contains('active')) renderKPIs()
        }).catch(() => { store._kpiFormsLoaded = false }) // allow retry on failure
    }

    const { kpiPeriod, kpiShowCompare } = store
    const range = getKPIPeriodRange(kpiPeriod)
    const summary = calcSummaryCards(store.leads, range)
    const funnel = calcConversionFunnel(store.leads)
    const sources = calcSourceBreakdown(store.leads)
    const types = calcTypeBreakdown(store.leads)
    const team = calcTeamPerformance(store.leads)
    const trend = calcMonthlyTrend(store.leads)
    const bottlenecks = calcStageBottlenecks(store.leads)
    const payments = calcPaymentHealth(store.leads)
    const forms = calcFormConversion()

    const fmt = window.fmt || (n => '$' + (n || 0).toLocaleString())
    const esc = window.escHtml || (s => s)

    // Period tabs
    const periods = [
        { id: 'mes', label: 'Este Mes' },
        { id: 'ultimoMes', label: 'Mes Anterior' },
        { id: 'trimestre', label: 'Trimestre' },
        { id: 'año', label: 'Este Año' },
        { id: 'custom', label: 'Personalizado' }
    ]

    let html = `
        <div style="display:flex;align-items:center;gap:16px;margin-bottom:16px;flex-wrap:wrap;">
            <div class="period-tabs" style="margin-bottom:0;">
                ${periods.map(p => `<button class="period-tab ${kpiPeriod === p.id ? 'active' : ''}" onclick="setKPIPeriod('${p.id}')">${p.label}</button>`).join('')}
            </div>
            <label style="display:flex;align-items:center;gap:6px;font-size:12px;color:var(--text-2);cursor:pointer;">
                <input type="checkbox" ${kpiShowCompare ? 'checked' : ''} onchange="toggleKPICompare()" style="accent-color:var(--purple);">
                vs Periodo Anterior
            </label>
            ${kpiPeriod === 'custom' ? `
                <div style="display:flex;gap:8px;align-items:center;">
                    <input type="date" id="kpiCustomStart" value="${store.kpiCustomStart || ''}" onchange="setKPICustomRange()" style="padding:6px 10px;border-radius:6px;border:1px solid var(--border);background:var(--bg-2);color:var(--text-1);font-size:12px;">
                    <span style="color:var(--text-3);">a</span>
                    <input type="date" id="kpiCustomEnd" value="${store.kpiCustomEnd || ''}" onchange="setKPICustomRange()" style="padding:6px 10px;border-radius:6px;border:1px solid var(--border);background:var(--bg-2);color:var(--text-1);font-size:12px;">
                </div>
            ` : ''}
        </div>
    `

    // Section 1: Summary Cards
    html += `<div class="stats-row">`
    html += summaryCard('Leads Nuevos', summary.newLeads, '', summary.showCompare ? deltaBadge(summary.newLeads, summary.prevNew) : '')
    html += summaryCard('Tasa Conversion', summary.convRate.toFixed(1) + '%', 'green', summary.showCompare && summary.prevConvRate !== null ? deltaBadge(summary.convRate, summary.prevConvRate) : '')
    html += summaryCard('Ventas Cerradas', fmt(summary.totalVenta), 'green', summary.showCompare ? deltaBadge(summary.totalVenta, summary.prevVenta) : '')
    html += summaryCard('M2M Revenue', fmt(summary.totalM2M), 'green', summary.showCompare ? deltaBadge(summary.totalM2M, summary.prevM2M) : '')
    html += summaryCard('Ticket Promedio', fmt(summary.avgTicket), '', summary.showCompare && summary.prevAvgTicket !== null ? deltaBadge(summary.avgTicket, summary.prevAvgTicket) : '')
    html += summaryCard('Ciclo de Venta', summary.avgCycle > 0 ? Math.round(summary.avgCycle) + 'd' : '-', '', summary.showCompare && summary.prevAvgCycle !== null ? deltaBadge(summary.avgCycle, summary.prevAvgCycle, '', true) : '')
    html += `</div>`

    // Section 2: Conversion Funnel
    const maxFunnel = Math.max(...funnel.groups.map(g => g.count), 1)
    // Build funnel with interleaved arrows as separate elements
    let funnelItems = ''
    funnel.groups.forEach((g, i) => {
        funnelItems += `
            <div class="kpi-funnel-step">
                <div class="kpi-funnel-bar-wrap">
                    <div class="kpi-funnel-bar" style="width:${Math.max(8, g.count / maxFunnel * 100)}%;"></div>
                    <span class="kpi-funnel-count">${g.count}</span>
                </div>
                <div class="kpi-funnel-label">${g.label}</div>
            </div>`
        if (i < funnel.groups.length - 1) {
            funnelItems += `<div class="kpi-funnel-arrow">${funnel.groups[i + 1].pct.toFixed(0)}%</div>`
        }
    })

    html += `
        <div class="dash-card" style="margin-bottom:16px;">
            <h3>Embudo de Conversion</h3>
            <div class="kpi-funnel">${funnelItems}</div>
        </div>
    `

    // Section 3: Grid
    html += `<div class="dashboard-grid">`

    // Card A: Sources
    const maxSource = Math.max(...sources.map(s => s.total), 1)
    html += `
        <div class="dash-card">
            <h3>Leads por Fuente</h3>
            ${sources.length === 0 ? '<div class="kpi-empty">Sin datos</div>' : sources.map(s => `
                <div class="kpi-bar-row">
                    <div class="kpi-bar-label">${esc(s.name)}</div>
                    <div class="kpi-bar-track">
                        <div class="kpi-bar-fill" style="width:${s.total / maxSource * 100}%;"></div>
                    </div>
                    <div class="kpi-bar-val">${s.total}</div>
                    <div class="kpi-bar-sub ${s.convRate >= 50 ? 'good' : s.convRate >= 25 ? 'ok' : 'low'}">${s.convRate.toFixed(0)}%</div>
                </div>
            `).join('')}
        </div>
    `

    // Card B: Types
    const maxType = Math.max(...types.map(t => t.venta), 1)
    html += `
        <div class="dash-card">
            <h3>Leads por Tipo de Viaje</h3>
            ${types.length === 0 ? '<div class="kpi-empty">Sin datos</div>' : types.map(t => `
                <div class="kpi-bar-row">
                    <div class="kpi-bar-label">${esc(t.name)}</div>
                    <div class="kpi-bar-track">
                        <div class="kpi-bar-fill type" style="width:${t.venta / maxType * 100}%;"></div>
                    </div>
                    <div class="kpi-bar-val">${t.count}</div>
                    <div class="kpi-bar-sub">${fmt(t.venta)}</div>
                </div>
            `).join('')}
        </div>
    `

    // Card C: Team
    html += `
        <div class="dash-card">
            <h3>Rendimiento del Equipo</h3>
            <div style="overflow-x:auto;">
            <table class="kpi-table">
                <thead><tr><th>Persona</th><th>Leads</th><th>Cerr.</th><th>Conv.</th><th>M2M</th><th>Prom.</th></tr></thead>
                <tbody>
                    ${team.length === 0 ? '<tr><td colspan="6" style="text-align:center;color:var(--text-3);">Sin datos</td></tr>' : team.map(t => `
                        <tr>
                            <td>${esc(t.name)}</td>
                            <td>${t.assigned}</td>
                            <td>${t.closed}</td>
                            <td class="${t.convRate >= 50 ? 'good' : t.convRate >= 25 ? 'ok' : 'low'}">${t.convRate.toFixed(0)}%</td>
                            <td style="color:var(--green)">${fmt(t.m2m)}</td>
                            <td>${fmt(t.avgTicket)}</td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>
            </div>
        </div>
    `

    // Card D: Monthly Trend
    const maxCreated = Math.max(...trend.map(m => m.created), 1)
    const maxM2M = Math.max(...trend.map(m => m.m2m), 1)
    html += `
        <div class="dash-card">
            <h3>Tendencia Mensual (6 meses)</h3>
            ${trend.map((m, i) => {
                const prev = i > 0 ? trend[i - 1] : null
                const growth = prev && prev.m2m > 0 ? ((m.m2m - prev.m2m) / prev.m2m * 100) : null
                return `
                <div class="kpi-trend-row">
                    <div class="kpi-trend-label">${m.label}</div>
                    <div class="kpi-trend-bars">
                        <div class="kpi-trend-bar created" style="width:${m.created / maxCreated * 100}%;" title="Creados: ${m.created}"></div>
                        <div class="kpi-trend-bar closed" style="width:${m.closed / maxCreated * 100}%;" title="Cerrados: ${m.closed}"></div>
                    </div>
                    <div class="kpi-trend-val">${fmt(m.m2m)}</div>
                    ${growth !== null ? `<span class="kpi-delta small ${growth > 0 ? 'up' : growth < 0 ? 'down' : 'neutral'}">${growth > 0 ? '↑' : growth < 0 ? '↓' : '='} ${Math.abs(growth).toFixed(0)}%</span>` : ''}
                </div>`
            }).join('')}
            <div class="kpi-trend-legend">
                <span><span class="kpi-legend-dot created"></span> Creados</span>
                <span><span class="kpi-legend-dot closed"></span> Cerrados</span>
            </div>
        </div>
    `

    // Card E: Bottlenecks
    html += `
        <div class="dash-card">
            <h3>Pipeline — Tiempo por Etapa</h3>
            ${bottlenecks.length === 0 ? '<div class="kpi-empty">Sin datos</div>' : bottlenecks.map(b => {
                const isBottleneck = b.avgDays > 14
                const isCritical = b.avgDays > 30
                return `
                <div class="kpi-bar-row">
                    <div class="kpi-bar-label">
                        <span class="kpi-stage-dot" style="background:${b.color};"></span>
                        ${esc(b.name)}
                    </div>
                    <div class="kpi-bar-val">${b.count}</div>
                    <div class="kpi-bar-sub ${isCritical ? 'critical' : isBottleneck ? 'warning' : ''}">${Math.round(b.avgDays)}d prom.</div>
                </div>`
            }).join('')}
        </div>
    `

    // Card F: Payment Health
    const collPct = Math.min(100, payments.collectionRate)
    html += `
        <div class="dash-card">
            <h3>Depositos y Pagos</h3>
            <div class="kpi-payment-grid">
                <div class="kpi-payment-item">
                    <div class="kpi-payment-label">Depositos Esperados</div>
                    <div class="kpi-payment-val">${fmt(payments.totalExpected)}</div>
                </div>
                <div class="kpi-payment-item">
                    <div class="kpi-payment-label">Depositos Cobrados</div>
                    <div class="kpi-payment-val" style="color:var(--green)">${fmt(payments.totalCollected)}</div>
                </div>
            </div>
            <div class="kpi-progress-wrap">
                <div class="kpi-progress-bar">
                    <div class="kpi-progress-fill" style="width:${collPct}%;background:${collPct > 70 ? 'var(--green)' : collPct > 40 ? 'var(--yellow)' : 'var(--red)'};"></div>
                </div>
                <span class="kpi-progress-label">${collPct.toFixed(0)}% cobrado</span>
            </div>
            <div class="kpi-payment-grid" style="margin-top:12px;">
                <div class="kpi-payment-item">
                    <div class="kpi-payment-label">Pagos Vencidos</div>
                    <div class="kpi-payment-val" style="color:${payments.overdue > 0 ? 'var(--red)' : 'var(--text-2)'}">${payments.overdue}</div>
                </div>
                <div class="kpi-payment-item">
                    <div class="kpi-payment-label">Pendientes de Pago</div>
                    <div class="kpi-payment-val" style="color:var(--yellow)">${payments.pendingPayment}</div>
                </div>
            </div>
        </div>
    `

    html += `</div>` // close dashboard-grid

    // Section 4: Form Analytics
    html += `
        <div class="dash-card" style="margin-top:16px;">
            <h3>Formularios Web</h3>
            <div class="stats-row" style="margin-bottom:16px;">
                <div class="stat-card"><div class="stat-val">${forms.total}</div><div class="stat-label">Total Enviados</div></div>
                <div class="stat-card"><div class="stat-val green">${forms.converted}</div><div class="stat-label">Convertidos</div></div>
                <div class="stat-card"><div class="stat-val yellow">${forms.pending}</div><div class="stat-label">Pendientes</div></div>
                <div class="stat-card"><div class="stat-val" style="color:var(--red)">${forms.abandoned}</div><div class="stat-label">Abandonados</div></div>
            </div>
            ${forms.types.length > 0 ? `
                <h4 style="font-size:13px;color:var(--text-2);margin:0 0 8px;">Conversion por Tipo</h4>
                ${forms.types.map(t => `
                    <div class="kpi-bar-row">
                        <div class="kpi-bar-label">${esc(t.name)}</div>
                        <div class="kpi-bar-track">
                            <div class="kpi-bar-fill form" style="width:${t.rate}%;"></div>
                        </div>
                        <div class="kpi-bar-val">${t.converted}/${t.total}</div>
                        <div class="kpi-bar-sub ${t.rate >= 50 ? 'good' : t.rate >= 25 ? 'ok' : 'low'}">${t.rate.toFixed(0)}%</div>
                    </div>
                `).join('')}
            ` : ''}
        </div>
    `

    container.innerHTML = html
}

function summaryCard(label, value, colorClass, delta) {
    return `
        <div class="stat-card">
            <div class="stat-val ${colorClass}">${value}</div>
            <div class="stat-label">${label}</div>
            ${delta ? `<div style="margin-top:4px;">${delta}</div>` : ''}
        </div>
    `
}

// ========== CONTROLS ==========

function setKPIPeriod(period) {
    store.kpiPeriod = period
    renderKPIs()
}

function toggleKPICompare() {
    store.kpiShowCompare = !store.kpiShowCompare
    renderKPIs()
}

function setKPICustomRange() {
    store.kpiCustomStart = document.getElementById('kpiCustomStart')?.value || null
    store.kpiCustomEnd = document.getElementById('kpiCustomEnd')?.value || null
    renderKPIs()
}

// ========== WINDOW BRIDGE ==========
Object.assign(window, {
    renderKPIs,
    setKPIPeriod,
    toggleKPICompare,
    setKPICustomRange,
})
